(() => {
  "use strict";
  const PREFS_KEY = "gllf_smart_preferences_v1";
  const MODE_KEY = "gllf_smart_mode_v1";
  const GUEST_TOKEN_KEY = "gllf_guest_token_v1";
  const ZONE_KEYS = ["gllf_hardiness_zone", "gllf_zone", "gllf-zone", "gllfZone", "hardiness_zone", "hardinessZone", "usda_zone", "usdaZone"];
  const RESTRICTED_STATES = new Set(["AZ", "CA", "HI", "ID", "NV", "OR", "UT", "WA"]);
  const aliases = {
    zone: /hardiness|usda.*zone|zone/i,
    fragrance: /fragrance/i,
    culinary: /culinary/i,
    oil: /essential.?oil|oil suitability/i,
    pollinator: /pollinator/i,
    bloom: /bloom pattern|bloom season|bloom window/i,
    growth: /growth habit|habit/i,
    space: /suitable space|growing space/i,
    characteristics: /plant characteristics|characteristics/i,
    format: /plant size\s*&\s*form|plant format|product format|format/i,
    beginner: /beginner/i,
    pickup: /pickup|fulfillment/i
  };
  let pendingController = null;

  function parseJson(node, fallback = {}) {
    try { return JSON.parse(node?.textContent || ""); } catch (_) { return fallback; }
  }
  function text(value) {
    if (value == null) return "";
    if (Array.isArray(value)) return value.map(text).filter(Boolean).join("; ");
    if (typeof value === "object") return text(value.label || value.name || value.value || value.handle || "");
    return String(value);
  }
  function normalizeZone(value) {
    const match = text(value).toLowerCase().match(/(?:^|\s)((?:[1-9]|1[0-3])[ab])(?:$|\s)/);
    return match?.[1] || "";
  }
  function readStoredObject(key) {
    try {
      const value = localStorage.getItem(key);
      if (!value) return null;
      try { return JSON.parse(value); } catch (_) { return value; }
    } catch (_) { return null; }
  }
  function storedZone() {
    for (const key of ZONE_KEYS) {
      const raw = readStoredObject(key);
      const zone = normalizeZone(raw?.zone || raw?.hardinessZone || raw?.usdaZone || raw?.value || raw);
      if (zone) return zone;
    }
    return "";
  }
  function storedDestination() {
    const keys = ["gllf_destination_v1", "gllf_plant_destination", "gllf_hardiness_zone"];
    for (const key of keys) {
      const raw = readStoredObject(key);
      if (raw && typeof raw === "object") return raw;
    }
    return {};
  }
  function saveZone(zone, zip = "", source = "smart-shop") {
    const payload = { zone, hardinessZone: zone, zip, source, updatedAt: Date.now() };
    try {
      localStorage.setItem("gllf_hardiness_zone", JSON.stringify(payload));
      localStorage.setItem("hardinessZone", zone);
    } catch (_) {}
    ["gllf:zone-change", "gllf:zone-changed", "gllf:hardiness-zone-change", "gllf-zone-updated"].forEach(name => document.dispatchEvent(new CustomEvent(name, { detail: payload })));
  }
  function readPreferences() {
    try { return JSON.parse(sessionStorage.getItem(PREFS_KEY) || "null"); } catch (_) { return null; }
  }
  function savePreferences(preferences) {
    try { sessionStorage.setItem(PREFS_KEY, JSON.stringify(preferences)); } catch (_) {}
  }
  function findFilter(config, alias) {
    const pattern = aliases[alias];
    return pattern ? config.filters.find(filter => pattern.test(filter.label) || pattern.test(filter.paramName)) : null;
  }
  function matchingValues(filter, patterns) {
    if (!filter) return [];
    return filter.values.filter(value => value.count > 0 && patterns.some(pattern => pattern.test(value.label) || pattern.test(text(value.value))));
  }
  function addValues(url, filter, values) {
    if (!filter || !values.length) return false;
    url.searchParams.delete("page");
    for (const value of values) {
      const name = value.paramName || filter.paramName;
      if (!name) continue;
      if (!url.searchParams.getAll(name).includes(text(value.value))) url.searchParams.append(name, text(value.value));
    }
    return true;
  }
  function matchZoneValues(filter, zone) {
    const escaped = zone.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return matchingValues(filter, [new RegExp(`(^|[^0-9])${escaped}([^a-z0-9]|$)`, "i")]);
  }
  function availableConsent() {
    if (navigator.globalPrivacyControl) return false;
    const privacy = window.Shopify?.customerPrivacy;
    if (privacy?.analyticsProcessingAllowed) return Boolean(privacy.analyticsProcessingAllowed());
    return false;
  }
  async function ensureGuestSession(root) {
    const existing = localStorage.getItem(GUEST_TOKEN_KEY);
    if (existing || window.GLLFSavedConfig?.customerId) return existing;
    try {
      const response = await fetch("/apps/gllf-saved/session", { method: "POST", headers: { "content-type": "application/json" }, body: "{}" });
      if (!response.ok) return null;
      const data = await response.json();
      if (data.guestToken) localStorage.setItem(GUEST_TOKEN_KEY, data.guestToken);
      return data.guestToken || null;
    } catch (_) { return null; }
  }
  async function analytics(root, type, payload = {}) {
    if (!root?.dataset?.analyticsEndpoint) return;
    if (!availableConsent()) return;
    try {
      const token = await ensureGuestSession(root);
      const headers = { "content-type": "application/json", "idempotency-key": crypto.randomUUID() };
      if (token) headers["x-gllf-guest-token"] = token;
      await fetch(root.dataset.analyticsEndpoint, {
        method: "POST", headers, keepalive: true,
        body: JSON.stringify({ type, consent: true, payload: { ...payload, mode: root.dataset.mode || "garden", zoneState: storedZone() ? "known" : "unknown" } })
      });
    } catch (_) {}
  }
  function announce(root, message) {
    const live = root.querySelector("[data-gllf-live]");
    if (!live) return;
    live.textContent = "";
    requestAnimationFrame(() => { live.textContent = message; });
  }
  function nativeFilterButton() {
    return document.querySelector(".mobile-facets__open, .facets__open, [data-open-filter], #main-collection-filters summary, #main-search-filters summary");
  }
  function openNativeFilters(root) {
    analytics(root, "filter_open", { context: root.dataset.context });
    const button = nativeFilterButton();
    if (button) button.click();
    else document.querySelector("#main-collection-filters, #main-search-filters")?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  }
  async function renderUrl(root, target, push = true, eventType = "filter_selection") {
    const url = new URL(target, location.href);
    const currentY = scrollY;
    pendingController?.abort();
    pendingController = new AbortController();
    root.setAttribute("aria-busy", "true");
    try {
      const renderUrl = new URL(url);
      renderUrl.searchParams.set("section_id", root.dataset.sectionId);
      const response = await fetch(renderUrl, { headers: { "x-requested-with": "XMLHttpRequest" }, signal: pendingController.signal });
      if (!response.ok) throw new Error("section_render_failed");
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const nextRoot = parsed.querySelector(`[data-gllf-smart-shop][data-section-id="${CSS.escape(root.dataset.sectionId)}"]`);
      const nextGrid = parsed.querySelector("#ProductGridContainer");
      const nextCollectionFilters = parsed.querySelector("#main-collection-filters, #main-search-filters");
      const currentGrid = document.querySelector("#ProductGridContainer");
      const currentFilters = document.querySelector("#main-collection-filters, #main-search-filters");
      if (!nextRoot || !nextGrid || !currentGrid) throw new Error("section_markup_missing");
      if (nextCollectionFilters && currentFilters) currentFilters.replaceWith(nextCollectionFilters);
      else if (!nextCollectionFilters && currentFilters) currentFilters.remove();
      else if (nextCollectionFilters && !currentFilters) root.before(nextCollectionFilters);
      root.replaceWith(nextRoot);
      currentGrid.replaceWith(nextGrid);
      url.searchParams.delete("section_id");
      if (push) history.pushState({ gllfSmartShop: true, y: currentY }, "", url);
      initRoot(nextRoot);
      window.scrollTo({ top: currentY, behavior: "auto" });
      const count = nextRoot.dataset.resultCount || "0";
      announce(nextRoot, `${count} products shown.`);
      analytics(nextRoot, eventType, { resultCount: Number(count), query: url.search });
    } catch (error) {
      if (error.name === "AbortError") return;
      url.searchParams.delete("section_id");
      location.assign(url.href);
    } finally { root.removeAttribute("aria-busy"); }
  }
  function applyQuick(root, config, kind) {
    const url = new URL(location.href);
    const notes = [];
    let applied = false;
    const zone = storedZone();
    if (kind === "zone" || kind === "cold") {
      if (!zone) {
        root.querySelector("[data-gllf-finder-open]")?.click();
        announce(root, "Choose or detect your hardiness zone first.");
        return;
      }
      applied = addValues(url, findFilter(config, "zone"), matchZoneValues(findFilter(config, "zone"), zone)) || applied;
      if (kind === "cold") applied = addValues(url, findFilter(config, "characteristics"), matchingValues(findFilter(config, "characteristics"), [/cold.?hardy/i])) || applied;
    }
    if (kind === "fragrance") applied = addValues(url, findFilter(config, "fragrance"), matchingValues(findFilter(config, "fragrance"), [/exceptional/i, /^strong$/i])) || applied;
    if (kind === "culinary") applied = addValues(url, findFilter(config, "culinary"), matchingValues(findFilter(config, "culinary"), [/excellent/i, /^great$/i, /^good$/i])) || applied;
    if (kind === "pollinator") applied = addValues(url, findFilter(config, "pollinator"), matchingValues(findFilter(config, "pollinator"), [/excellent/i, /^great$/i])) || applied;
    if (kind === "bloom") applied = addValues(url, findFilter(config, "bloom"), matchingValues(findFilter(config, "bloom"), [/long season/i, /everbloom/i, /continuous/i, /repeat bloom/i])) || applied;
    if (kind === "beginner") applied = addValues(url, findFilter(config, "beginner"), matchingValues(findFilter(config, "beginner"), [/yes|beginner|easy/i])) || applied;
    if (kind === "wholesale") applied = addValues(url, findFilter(config, "format"), matchingValues(findFilter(config, "format"), [/tray|plug/i])) || applied;
    if (kind === "pickup") {
      const prefs = { ...(readPreferences() || {}), pickup: true, mode: root.dataset.mode || "garden", updatedAt: Date.now() };
      savePreferences(prefs); enhanceCards(root, prefs); applied = true; notes.push("Pickup preference applied; checkout confirms location eligibility and time.");
    }
    if (!applied) {
      announce(root, "That shortcut is unavailable because this collection does not expose the required Shopify filter.");
      return;
    }
    if (notes.length) announce(root, notes.join(" "));
    analytics(root, "quick_path_selection", { intent: kind });
    if (url.href !== location.href) renderUrl(root, url, true, "filter_selection");
  }
  async function lookupZone(root, zip, status) {
    if (!/^\d{5}$/.test(zip)) { status.textContent = "Enter a five-digit U.S. ZIP code."; return ""; }
    status.textContent = "Detecting zone…";
    const endpoints = [`${root.dataset.zoneEndpoint}?zip=${encodeURIComponent(zip)}`, root.dataset.zoneTable];
    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, { headers: { accept: "application/json" }, signal: AbortSignal.timeout(8000) });
        if (!response.ok) continue;
        const data = await response.json();
        const zone = normalizeZone(endpoint === root.dataset.zoneTable ? data[zip] : data.zone || data.hardinessZone || data.value);
        if (zone) { saveZone(zone, zip, endpoint === root.dataset.zoneTable ? "bundled-2023" : "zone-app"); status.textContent = `ZIP ${zip} is mapped to Zone ${zone.toUpperCase()}. Confirm the ZIP if this looks unexpected.`; return zone; }
      } catch (_) {}
    }
    status.textContent = "Zone lookup is unavailable. Choose your USDA zone manually to continue.";
    return "";
  }
  function initFinder(root, config) {
    const dialog = root.querySelector("[data-gllf-finder]");
    if (!dialog) return;
    const form = dialog.querySelector("[data-gllf-finder-form]");
    const zoneSelect = form.elements.zone;
    const zipInput = form.elements.zip;
    const status = dialog.querySelector("[data-gllf-zone-status]");
    root.querySelector("[data-gllf-finder-open]")?.addEventListener("click", () => {
      const zone = storedZone(); if (zone) zoneSelect.value = zone;
      dialog.showModal();
      requestAnimationFrame(() => (zone ? zoneSelect : zoneSelect).focus());
      analytics(root, "guided_finder_open", { zoneState: zone ? "known" : "unknown" });
    });
    dialog.querySelectorAll("[data-gllf-finder-close]").forEach(button => button.addEventListener("click", () => dialog.close()));
    dialog.addEventListener("click", event => { if (event.target === dialog) dialog.close(); });
    dialog.querySelector("[data-gllf-zone-lookup]")?.addEventListener("click", async () => {
      const zone = await lookupZone(root, zipInput.value.trim(), status);
      if (zone) zoneSelect.value = zone;
    });
    form.addEventListener("submit", event => {
      event.preventDefault();
      const data = new FormData(form), zone = normalizeZone(data.get("zone"));
      if (zone) saveZone(zone, String(data.get("zip") || ""), "smart-shop");
      const preferences = {
        zone,
        intents: data.getAll("intent"),
        traits: data.getAll("trait"),
        format: text(data.get("format")),
        mode: root.dataset.mode || "garden",
        updatedAt: Date.now()
      };
      const url = new URL(location.href), applied = [], skipped = [];
      const map = [
        [zone, "zone", filter => matchZoneValues(filter, zone), `Zone ${zone.toUpperCase()}`],
        [preferences.intents.includes("culinary"), "culinary", filter => matchingValues(filter, [/excellent/i, /^great$/i, /^good$/i]), "culinary use"],
        [preferences.intents.includes("fragrance"), "fragrance", filter => matchingValues(filter, [/exceptional/i, /^strong$/i]), "strong fragrance"],
        [preferences.intents.includes("pollinator"), "pollinator", filter => matchingValues(filter, [/excellent/i, /^great$/i]), "pollinator value"],
        [preferences.intents.includes("oil"), "oil", filter => matchingValues(filter, [/excellent/i, /^great$/i, /^good$/i]), "essential-oil suitability"],
        [preferences.intents.includes("bloom") || preferences.traits.includes("repeat"), "bloom", filter => matchingValues(filter, [/long season/i, /everbloom/i, /continuous/i, /repeat bloom/i]), "extended bloom"],
        [preferences.traits.includes("compact"), "growth", filter => matchingValues(filter, [/compact/i]), "compact habit"],
        [preferences.traits.includes("upright"), "growth", filter => matchingValues(filter, [/upright/i]), "upright habit"],
        [preferences.traits.includes("indoor"), "space", filter => matchingValues(filter, [/indoor/i]), "indoor suitability"],
        [preferences.traits.includes("drought"), "characteristics", filter => matchingValues(filter, [/drought/i]), "drought resistance"],
        [Boolean(preferences.format), "format", filter => matchingValues(filter, [new RegExp(preferences.format.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i")]), `${preferences.format} format`]
      ];
      for (const [wanted, alias, valuesFor, reason] of map) {
        if (!wanted) continue;
        const filter = findFilter(config, alias), values = filter ? valuesFor(filter) : [];
        if (addValues(url, filter, values)) applied.push(reason); else skipped.push(reason);
      }
      preferences.applied = applied;
      savePreferences(preferences);
      dialog.querySelector("[data-gllf-finder-explain]").textContent = `${applied.length ? `Applied: ${applied.join(", ")}.` : "No guided criteria could be applied."}${skipped.length ? ` Not available in this collection: ${skipped.join(", ")}.` : ""}`;
      analytics(root, "guided_finder_completion", { intent: preferences.intents.join("|"), format: preferences.format || "any", traits: preferences.traits.join("|"), appliedCount: applied.length, skippedCount: skipped.length });
      dialog.close();
      if (applied.length) renderUrl(root, url, true, "guided_finder_results"); else enhanceCards(root, preferences);
    });
  }
  function valueContains(value, pattern) { return pattern.test(text(value)); }
  function zoneSupported(value, zone) {
    if (!zone) return false;
    const normalized = text(value).toLowerCase();
    return new RegExp(`(^|[^0-9])${zone}([^a-z0-9]|$)`, "i").test(normalized);
  }
  function scoreCard(data, preferences) {
    const reasons = [], checks = [];
    if (preferences?.zone) checks.push([zoneSupported(data.hardiness, preferences.zone), `listed for Zone ${preferences.zone.toUpperCase()}`]);
    for (const intent of preferences?.intents || []) {
      if (intent === "culinary") checks.push([/good|great|excellent/i.test(text(data.culinary)) && !/not recommended/i.test(text(data.culinary)), "culinary rating"]);
      if (intent === "fragrance") checks.push([/strong|exceptional/i.test(text(data.fragrance)), "strong fragrance"]);
      if (intent === "pollinator") checks.push([/great|excellent/i.test(text(data.pollinator)), "pollinator rating"]);
      if (intent === "oil") checks.push([/good|great|excellent/i.test(text(data.oil)), "oil suitability"]);
      if (intent === "bloom") checks.push([/repeat|long|continuous|everbloom/i.test(text(data.bloomPattern)), "extended bloom pattern"]);
    }
    for (const trait of preferences?.traits || []) {
      if (trait === "compact") checks.push([valueContains(data.growthHabit, /compact/i), "compact habit"]);
      if (trait === "upright") checks.push([valueContains(data.growthHabit, /upright/i), "upright habit"]);
      if (trait === "indoor") checks.push([valueContains(data.suitableSpace, /indoor/i), "cataloged indoor use"]);
      if (trait === "drought") checks.push([valueContains(data.plantCharacteristics, /drought/i), "drought-resistant trait"]);
      if (trait === "repeat") checks.push([valueContains(data.bloomPattern, /repeat|long|continuous|everbloom/i), "repeat or long bloom"]);
    }
    if (preferences?.format) checks.push([(data.variants || []).some(v => text(v.format).toLowerCase().includes(preferences.format.toLowerCase())), `${preferences.format} format`]);
    for (const [matched, reason] of checks) if (matched) reasons.push(reason);
    const score = checks.length ? Math.round(reasons.length / checks.length * 100) : 0;
    const positive = (data.variants || []).filter(v => Number(v.inventoryQuantity) > 0);
    const sellable = (data.variants || []).filter(v => v.available);
    const tier = positive.length ? 0 : sellable.length ? 1 : 4;
    return { score, reasons: reasons.slice(0, 3), tier, positive, sellable };
  }
  function opportunity(data, ranked, preferences) {
    const selected = (data.variants || []).find(v => text(v.id) === text(data.selectedVariantId)) || data.variants?.[0];
    const selectedPositive = selected && Number(selected.inventoryQuantity) > 0;
    const now = new Date(), month = now.getMonth() + 1, summer = month >= 6 && month <= 9, bareRootSeason = month >= 3 && month <= 5;
    const state = text(storedDestination().state || storedDestination().provinceCode).toUpperCase();
    const hasBareRoot = (data.variants || []).some(v => /bare.?root/i.test(text(v.format) + " " + text(v.title)) && v.available);
    if (selectedPositive) return `In stock: ${text(selected.format || selected.title)}.`;
    if (ranked.positive.length) return `Another size is in stock: ${text(ranked.positive[0].format || ranked.positive[0].title)}.`;
    if (state && RESTRICTED_STATES.has(state)) return hasBareRoot && bareRootSeason ? "Live-plant shipping is restricted to this state; an eligible soil-free bare-root path may be reviewed at checkout." : "Live-plant shipping is restricted to this state. Check pickup, save, alert or propagation-interest paths.";
    if (summer && data.isPlant) return "Summer live-plant shipping is paused. Check eligible local pickup, save or alert paths.";
    if (hasBareRoot && bareRootSeason) return "An eligible soil-free bare-root format may be available; checkout confirms destination eligibility.";
    if (ranked.sellable.length) return `Reservation or continued-selling path: ${text(ranked.sellable[0].format || ranked.sellable[0].title)}. Confirm timing before checkout.`;
    if (preferences?.pickup) return "Not currently sellable online. Check authoritative pickup availability, save it or request an alert.";
    return "Not currently available. Save it, request a cultivar alert or record propagation interest.";
  }
  function flowerColor(value) {
    const color = text(value).toLowerCase();
    if (color.includes("white")) return "#f3f0e9";
    if (color.includes("pink")) return "#d58ca3";
    if (color.includes("blue")) return "#6278b3";
    if (color.includes("lilac") || color.includes("mauve")) return "#a77ca8";
    if (color.includes("yellow")) return "#d8b946";
    if (color.includes("red")) return "#9f3d50";
    return "#756489";
  }
  function reorderProductCards(cards) {
    const byGrid = new Map();
    for (const card of cards) {
      const item = card.node.closest("li.grid__item");
      const grid = item?.parentElement;
      if (!item || !grid) continue;
      if (!byGrid.has(grid)) byGrid.set(grid, []);
      byGrid.get(grid).push({ ...card, item });
    }
    for (const entries of byGrid.values()) {
      const sorted = [...entries].sort((a, b) => a.rank.tier - b.rank.tier || b.rank.score - a.rank.score || a.index - b.index);
      const markers = entries.map(({ item }) => { const marker = document.createComment("gllf-card-slot"); item.before(marker); return marker; });
      sorted.forEach((entry, index) => markers[index].replaceWith(entry.item));
    }
  }
  function enhanceCards(root, preferences = readPreferences()) {
    const cards = [];
    document.querySelectorAll("[data-gllf-smart-card]").forEach((node, index) => {
      const data = parseJson(node, null); if (!data) return;
      const wrapper = node.closest(".card-wrapper, product-info, .product-card-wrapper") || node.parentElement;
      const panel = wrapper?.querySelector("[data-gllf-card-intelligence]"); if (!panel) return;
      const rank = scoreCard(data, preferences);
      const scoreBox = panel.querySelector("[data-gllf-card-match]");
      if (preferences && (preferences.zone || preferences.intents?.length || preferences.traits?.length || preferences.format)) {
        scoreBox.hidden = false;
        panel.querySelector("[data-gllf-card-score]").textContent = `${rank.score}% match`;
        panel.querySelector("[data-gllf-card-reasons]").textContent = rank.reasons.length ? `Why: ${rank.reasons.join(", ")}` : "No selected preference is confirmed by this product’s catalog fields.";
      } else scoreBox.hidden = true;
      const opportunityNode = panel.querySelector("[data-gllf-card-opportunity]");
      if (opportunityNode) opportunityNode.textContent = opportunity(data, rank, preferences);
      panel.querySelectorAll("[data-gllf-flower-color]").forEach(swatch => swatch.style.setProperty("--gllf-flower", flowerColor(data.flowerColor)));
      cards.push({ node, data, rank, index });
      if (!node.dataset.gllfClickBound) {
        node.dataset.gllfClickBound = "true";
        wrapper?.querySelectorAll("a[href*='/products/']").forEach(link => link.addEventListener("click", () => analytics(root, "smart_shop_product_click", { productId: data.productId, matchScore: rank.score, availabilityTier: rank.tier })));
      }
    });
    if (preferences && cards.length) {
      reorderProductCards(cards);
      analytics(root, "match_score_exposure", { productCount: cards.length, format: preferences.format || "any", intent: (preferences.intents || []).join("|") });
    }
  }
  function configureQuickPaths(root, config) {
    const required = { fragrance: "fragrance", culinary: "culinary", pollinator: "pollinator", bloom: "bloom", beginner: "beginner", wholesale: "format" };
    for (const [kind, alias] of Object.entries(required)) {
      const button = root.querySelector(`[data-gllf-quick="${kind}"]`);
      if (button && !findFilter(config, alias)) {
        button.disabled = true;
        button.title = "This collection does not expose the required Shopify filter.";
      }
    }
  }
  function contextualizeNativeFilters(root, mode) {
    const aside = document.querySelector("#main-collection-filters, #main-search-filters"); if (!aside) return;
    const technical = /rooting|vernalization|plant size\s*&\s*form|plant format|product format/i, botanical = /botanical group|plant type|hardiness|growth habit|flower color|plant characteristics|suitable space|preferred soil|sunlight|watering|mature height|mature spread|bloom|fragrance|culinary|essential.?oil|pollinator|deer resistance|rooting|vernalization|plant size\s*&\s*form|plant format|product format/i;
    aside.querySelectorAll("details, .facets__disclosure, .mobile-facets__details").forEach(group => {
      const label = group.querySelector("summary, .facets__heading, .mobile-facets__summary")?.textContent || "";
      const active = Boolean(group.querySelector("input:checked"));
      const hidden = !active && (root.dataset.context === "general" ? botanical.test(label) : mode === "garden" && technical.test(label));
      group.hidden = hidden;
      group.dataset.gllfContextHidden = String(hidden);
    });
  }
  function setLineProperty(form, name, value) {
    const fieldName = `properties[${name}]`;
    let input = [...form.elements].find(field => field.name === fieldName);
    if (!input) { input = document.createElement("input"); input.type = "hidden"; input.name = fieldName; form.append(input); }
    input.value = String(value || "");
  }
  function bindGlobalInstrumentation() {
    if (document.documentElement.dataset.gllfSmartAnalyticsBound) return;
    document.documentElement.dataset.gllfSmartAnalyticsBound = "true";
    const activeRoot = () => document.querySelector("[data-gllf-smart-shop]");
    document.addEventListener("submit", event => {
      const form = event.target.closest?.("form[action*='/cart/add']"); if (!form) return;
      const preference = readPreferences() || {}, selectedFormat = preference.format || form.closest(".card-wrapper, product-info")?.querySelector("[data-gllf-card-format]")?.textContent || "unknown";
      setLineProperty(form, "_gllf_smart_mode", preference.mode || sessionStorage.getItem(MODE_KEY) || "garden");
      setLineProperty(form, "_gllf_zone_state", storedZone() ? "known" : "unknown");
      setLineProperty(form, "_gllf_format", selectedFormat);
      setLineProperty(form, "_gllf_intent", (preference.intents || []).join("|") || "unknown");
    }, true);
    document.addEventListener("click", event => {
      const target = event.target.closest?.("[data-gllf-save],[data-gllf-compare],.quick-add__submit,[data-gllf-alert],[data-gllf-reservation],[data-gllf-preorder]");
      if (!target) return;
      const root = activeRoot(); if (!root) return;
      if (target.matches("[data-gllf-save]")) analytics(root, "save", { context: "product_card" });
      else if (target.matches("[data-gllf-compare]")) analytics(root, "compare", { context: "product_card" });
      else if (target.matches("[data-gllf-alert]")) analytics(root, "alert_subscribed", { context: "opportunity" });
      else if (target.matches("[data-gllf-reservation],[data-gllf-preorder]")) analytics(root, "reservation", { context: "opportunity" });
      else if (target.matches(".quick-add__submit")) analytics(root, target.getAttribute("aria-haspopup") === "dialog" ? "quick_view" : "quick_add", { context: "product_card" });
    }, true);
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) subscribe(PUB_SUB_EVENTS.cartUpdate, event => analytics(activeRoot(), "cart_addition", { productId: event?.productVariantId || "unknown" }));
  }
  function initRoot(root) {
    if (!root || root.dataset.gllfReady === "true") return;
    root.dataset.gllfReady = "true";
    const config = parseJson(root.querySelector("[data-gllf-filter-config]"), { filters: [] });
    let mode = sessionStorage.getItem(MODE_KEY) || "garden";
    if (!['garden', 'grower'].includes(mode)) mode = "garden";
    root.dataset.mode = mode;
    root.querySelectorAll("[data-gllf-mode]").forEach(button => {
      const active = button.dataset.gllfMode === mode; button.classList.toggle("is-active", active); button.setAttribute("aria-pressed", String(active));
      button.addEventListener("click", () => {
        mode = button.dataset.gllfMode; root.dataset.mode = mode; sessionStorage.setItem(MODE_KEY, mode);
        root.querySelectorAll("[data-gllf-mode]").forEach(item => { const selected = item === button; item.classList.toggle("is-active", selected); item.setAttribute("aria-pressed", String(selected)); });
        const help = root.querySelector("[data-gllf-mode-help]"); if (help) help.textContent = mode === "garden" ? "Garden mode emphasizes site fit, use, color and mature size." : "Grower mode emphasizes format, production traits, quantities and quote paths.";
        contextualizeNativeFilters(root, mode);
        analytics(root, "shopper_mode_change", { mode });
      });
    });
    root.querySelectorAll("[data-gllf-native-filter-open]").forEach(button => button.addEventListener("click", () => openNativeFilters(root)));
    root.querySelectorAll("[data-gllf-quick]:not(:disabled)").forEach(button => button.addEventListener("click", () => applyQuick(root, config, button.dataset.gllfQuick)));
    root.querySelectorAll("[data-gllf-filter-link]").forEach(link => link.addEventListener("click", event => { if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; event.preventDefault(); analytics(root, "filter_removal", { filter: link.dataset.filterLabel || "all" }); renderUrl(root, link.href, true, "filter_removal"); }));
    configureQuickPaths(root, config);
    contextualizeNativeFilters(root, mode);
    initFinder(root, config);
    bindGlobalInstrumentation();
    enhanceCards(root);
    if (Number(config.resultCount) === 0 && Number(config.activeCount) > 0) analytics(root, "zero_result_state", { query: location.search });
  }
  function initAll(scope = document) { scope.querySelectorAll("[data-gllf-smart-shop]").forEach(initRoot); }
  addEventListener("popstate", () => {
    const root = document.querySelector("[data-gllf-smart-shop]");
    if (root) renderUrl(root, location.href, false, "history_navigation");
  });
  document.addEventListener("shopify:section:load", event => initAll(event.target));
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initAll()); else initAll();
  window.GLLFSmartShop = Object.freeze({ init: initAll, version: "1.0.0-rc.1" });
})();

/* GLLF: open the guided finder from links ending in #find-my-plants (menu "Plant Finder"). */
(function () {
  function openFinder() {
    if (location.hash !== "#find-my-plants") return;
    var button = document.querySelector("[data-gllf-smart-shop] [data-gllf-finder-open]");
    if (button) { button.scrollIntoView({ block: "center" }); button.click(); }
  }
  function later() { setTimeout(openFinder, 400); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", later); else later();
  window.addEventListener("hashchange", openFinder);
})();
